// Utility functions for data processing in DataScience module

export const findSeriesByName = (allResults, name) => {
  return allResults.find((series) => series.name === name);
};

export const updateDataPointsOld = (existingDataPoints, newDataPoints) => {
  const dataPointMap = new Map();

  existingDataPoints.forEach((point) => {
    dataPointMap.set(point.value[0], point);
  });

  newDataPoints.forEach((newPoint) => {
    const time = newPoint.value[0];
    const existingPoint = dataPointMap.get(time);

    if (existingPoint) {
      if (
        parseInt(newPoint.interval.replace("s", "").replace("m", "")) <
        parseInt(existingPoint.interval.replace("s", "").replace("m", ""))
      ) {
        dataPointMap.set(time, newPoint);
      }
    } else {
      dataPointMap.set(time, newPoint);
    }
  });

  return Array.from(dataPointMap.values()).sort(
    (a, b) => new Date(a.value[0]) - new Date(b.value[0])
  );
};

export const updateDataPoints = (existingData, newDataPoints, startDate, endDate) => {
  if (!newDataPoints || newDataPoints.length === 0) {
    return existingData;
  }

  // Convert startDate and endDate to Date objects for comparison
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Determine the interval of the first new data point
  const newPointInterval = parseInt(newDataPoints[0].interval.replace("s", "").replace("m", ""));

  // Check if the interval of the first new data point is smaller or equal
  const firstExistingPoint = existingData.find(
    (point) => new Date(point.value[0]) >= start && new Date(point.value[0]) <= end
  );
  if (firstExistingPoint) {
    const existingPointInterval = parseInt(
      firstExistingPoint.interval.replace("s", "").replace("m", "")
    );
    if (newPointInterval > existingPointInterval) {
      return existingData; // Interval is larger, return existing data
    }
  }

  // Filter out existing data points that fall within the given time range
  const filteredData = existingData.filter((dataPoint) => {
    const date = new Date(dataPoint.value[0]);
    return date < start || date > end;
  });

  // Merge the new data points into the filtered data
  const updatedData = [...filteredData, ...newDataPoints];

  // Sort the merged data points by time
  updatedData.sort((a, b) => new Date(a.value[0]) - new Date(b.value[0]));

  return updatedData;
};

export const SENSOR_VARIABLE_MAP = {
  pulse_sensor: ["resistance", "temperature", "resis_temp_comp", "moisture_content"],
  soil_moisture_sensor: ["soil_moisture", "temperature", "soil_water_tension_vg"],
  soil_water_tension_sensor: ["soil_water_tension", "temperature", "soil_moisture_vg"],
  dendrometer_sensor: ["diameter", "tree_water_deficit", "diameter_minus_start"],
  tilt_sensor: ["roll", "pitch", "temperature"],
};

// Variables that require van-Genuchten parameters
const VAN_GENUCHTEN_VARIABLES = ["soil_water_tension_vg", "soil_moisture_vg"];

export const getCompatibleInstallations = (installations, variable) => {
  return installations.filter((inst) => {
    // Check if sensor type matches the variable
    const sensorMatches = Object.entries(SENSOR_VARIABLE_MAP).some(
      ([sensorKey, variables]) => inst.sensor[sensorKey] != null && variables.includes(variable)
    );
    if (!sensorMatches) return false;

    // For van-Genuchten variables, also check if the installation has VG params
    if (VAN_GENUCHTEN_VARIABLES.includes(variable)) {
      return inst.has_van_genuchten_params === true;
    }

    return true;
  });
};

export const chunkArray = (array, size) => {
  if (array.length == 0) return [[]];
  const chunkedArr = [];
  for (let i = 0; i < array.length; i += size) {
    chunkedArr.push(array.slice(i, i + size));
  }
  return chunkedArr;
};

export const getInterval = (startDate, endDate, customInterval = null) => {
  // If custom interval is provided, use it
  if (customInterval) {
    return customInterval;
  }

  const startDt = new Date(startDate);
  const endDt = new Date(endDate);
  const duration = endDt - startDt;

  // Duration in milliseconds
  const oneDay = 24 * 60 * 60 * 1000;
  const oneWeek = 7 * oneDay;
  const oneMonth = 30 * oneDay;

  if (duration <= oneDay) {
    return "5m"; // 5 minute intervals for 1 day
  } else if (duration <= oneWeek) {
    return "30m"; // 30 minute intervals for 1 week
  } else if (duration <= oneMonth) {
    return "2h"; // 2 hour intervals for 1 month
  } else {
    return "1d"; // 1 day intervals for longer periods
  }
};

export const getAvailableIntervals = (startDate, endDate) => {
  const startDt = new Date(startDate);
  const endDt = new Date(endDate);
  const duration = endDt - startDt;

  const oneDay = 24 * 60 * 60 * 1000;
  const oneWeek = 7 * oneDay;
  const oneMonth = 30 * oneDay;

  if (duration <= oneDay) {
    return { "5m": "5 minutes", "15m": "15 minutes", "30m": "30 minutes", "1h": "1 hour" };
  } else if (duration <= oneWeek) {
    return { "30m": "30 minutes", "1h": "1 hour", "2h": "2 hours", "6h": "6 hours" };
  } else if (duration <= oneMonth) {
    return { "2h": "2 hours", "6h": "6 hours", "12h": "12 hours", "1d": "1 day" };
  } else {
    return { "1d": "1 day", "7d": "1 week", "30d": "1 month" };
  }
};
